import React, { useState, useMemo } from 'react';

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ILegalTransactionFullObject } from '../Interfaces/ILegalTransaction';
import { ITaskFullObject, taskFullObjectSchema } from '../Interfaces/ITask';
import { ILegalClient } from '../Interfaces/ILegalClient';
import { IUser } from '../Interfaces/IUser';
import { IUserGroup } from '../Interfaces/IUserGroup';
import {
  useGetTaskPriority,
  useGetTaskType,
} from '../hooks/reactQuerys/TaskQueries';
import { EditTask } from './EditTask';
import { TaskRow } from './TaskRow';

interface IProps {
  legalTransaction?: ILegalTransactionFullObject;
  legalClient?: ILegalClient;
  tasks: ITaskFullObject[];
  setTasks: Function;
  legalClients?: ILegalClient[];
  users: IUser[];
  userGroups: IUserGroup[];
}

export const TaskTable: React.FC<IProps> = ({
  legalTransaction,
  legalClient,
  tasks,
  setTasks,
  legalClients,
  users,
  userGroups,
}) => {
  const { taskTypes, isLoadingTaskTypes } = useGetTaskType();
  const { taskPriorities, isLoadingTaskPriorities } = useGetTaskPriority();

  const [isOpenNew, setIsOpenNew] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [editTask, setEditTask] = useState(
    taskFullObjectSchema.parse({
      idLegalTransaction: legalTransaction
        ? legalTransaction.idLegalTransaction
        : null,
      idLegalClient: legalClient ? legalClient.idLegalClient : null,
    })
  );
  const [orgTask, setOrgTask] = useState<ITaskFullObject | null>(null);

  const { openTasks, closedTasks } = useMemo(() => {
    const open = tasks
      .filter(task => task.Closed_at === null)
      .sort((a, b) => {
        const da = a.Deadline_at ? new Date(a.Deadline_at).getTime() : Infinity;
        const db = b.Deadline_at ? new Date(b.Deadline_at).getTime() : Infinity;
        return da - db; // ✅ bald fällige zuerst
      });

    const closed = tasks.filter(task => task.Closed_at !== null);

    return { openTasks: open, closedTasks: closed };
  }, [tasks]);

  const handleNew = () => {
    let tmpId = -1;

    if (tasks.length > 0) {
      const tmpIdArray = Math.min(...tasks.map(t => t.idTask)) - 1;
      if (tmpIdArray <= tmpId) {
        tmpId = tmpIdArray - 1;
      }
    }

    setEditTask(
      taskFullObjectSchema.parse({
        idTask: tmpId,
        idLegalTransaction: legalTransaction
          ? legalTransaction.idLegalTransaction
          : null,
        idLegalClient: legalClient ? legalClient.idLegalClient : null,
        Users: legalTransaction?.Employee_idUser
          ? [legalTransaction.Employee_idUser]
          : [],
        UserGroups: legalTransaction?.idUserGroup
          ? [legalTransaction.idUserGroup]
          : [],
      })
    );
    setOrgTask(null);
    setIsOpenNew(true);
  };

  const handleOpen = (task: ITaskFullObject) => {
    setEditTask({ ...task });
    setOrgTask({ ...task });
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsOpenNew(false);
  };

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const foundObject = tasks.find(t => t.idTask === editTask.idTask);

    if (foundObject) {
      setTasks([
        ...tasks.map(t => (t.idTask === editTask.idTask ? editTask : t)),
      ]);
    } else {
      setTasks([...tasks, editTask]);
    }

    handleClose();
  };

  if (isLoadingTaskTypes || isLoadingTaskPriorities) {
    return <CircularProgress />;
  }

  if (!taskTypes || !taskPriorities) {
    return <Alert severity='error'>Es ist ein Fehler aufgetretten!</Alert>;
  }

  return (
    <>
      {(isOpen || isOpenNew) && (
        <Dialog open onClose={handleClose} maxWidth='md' fullWidth>
          <form onSubmit={handleSave}>
            <DialogContent>
              <EditTask
                editTask={editTask}
                setEditTask={setEditTask}
                taskPriority={taskPriorities}
                taskTypes={taskTypes}
                users={users}
                userGroups={userGroups}
                legalClients={legalClients}
                legalTransactionFullObj={legalTransaction}
                legalClient={legalClient}
                disableMarkDone={isOpenNew}
                disableEdit={
                  orgTask !== null &&
                  orgTask.Closed_at !== null &&
                  editTask.Closed_at !== null
                }
              />
            </DialogContent>
            <DialogActions>
              <Button variant='outlined' onClick={handleClose}>
                Abbruch
              </Button>
              <Button variant='contained' type='submit'>
                Übernehmen
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      )}

      <Typography variant='h5'>
        Aufgaben
        <Button
          type='button'
          variant='outlined'
          sx={{ float: 'right' }}
          onClick={handleNew}
        >
          Neue Aufgabe
        </Button>
      </Typography>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell width={20} />
            <TableCell width={50}>Art</TableCell>
            <TableCell width={50}>Priorität</TableCell>
            <TableCell width={90}>Frist</TableCell>
            <TableCell>Zugeordnete Mitarbeiter</TableCell>
            <TableCell width='40%'>Titel</TableCell>
            <TableCell width={70}>Aktionen</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {/* ✅ OFFENE TASKS (SORTIERT NACH DEADLINE) */}
          {openTasks.map(task => (
            <TaskRow
              key={`row-task-${task.idTask}`}
              task={task}
              tasks={tasks}
              setTasks={setTasks}
              users={users}
              userGroups={userGroups}
              legalClients={legalClients}
              taskPriorities={taskPriorities}
              taskTypes={taskTypes}
              legalTransaction={legalTransaction}
              legalClient={legalClient}
            />
          ))}

          {/* ✅ TRENNER NUR WENN ES ABGESCHLOSSENE GIBT */}
          {closedTasks.length > 0 && (
            <TableRow>
              <TableCell colSpan={7}>
                <Typography>Abgeschlossene Aufgaben</Typography>
              </TableCell>
            </TableRow>
          )}

          {/* ✅ ABGESCHLOSSENE TASKS */}
          {closedTasks.map(task => (
            <TaskRow
              key={`row-task-${task.idTask}`}
              task={task}
              tasks={tasks}
              setTasks={setTasks}
              users={users}
              userGroups={userGroups}
              legalClients={legalClients}
              taskPriorities={taskPriorities}
              taskTypes={taskTypes}
              legalTransaction={legalTransaction}
              legalClient={legalClient}
            />
          ))}
        </TableBody>
      </Table>
    </>
  );
};
